import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { api } from '@starling/core/api/client';
import type { components } from '@/api/types.gen';

export type Template = components['schemas']['Template'];
export type TemplateList = components['schemas']['TemplateList'];
export type TemplateCategory = components['schemas']['TemplateCategory'];

// M6-типы редактора шаблонов.
export type TemplateForEdit = components['schemas']['TemplateForEdit'];
export type TemplateForEditList = components['schemas']['TemplateForEditList'];
export type TemplateOverrideUpdate = components['schemas']['TemplateOverrideUpdate'];
export type TemplateBodies = TemplateOverrideUpdate['bodies'];

// Устаревший (M2) справочник шаблонов для SendMessageDialog. Эндпоинт с M6
// отдаёт TemplateForEditList, но диалог читает только code/category/variables/
// bodies — они присутствуют в обеих формах, поэтому шов совместим.
// Корневой префикс всех templates-запросов. Инвалидация по нему задевает и
// базовый справочник, и edit-подключи редактора (React Query матчит по префиксу
// от начала ключа) — единая точка сброса кэша после override/reset.
const TEMPLATES_KEY = ['templates'] as const;

export function useTemplatesQuery() {
  return useQuery({
    queryKey: TEMPLATES_KEY,
    queryFn: () => api.get<TemplateList>('/api/v1/admin/templates'),
    // Шаблоны меняются редко — большой staleTime.
    staleTime: 5 * 60_000,
  });
}

// M6 — список строк редактора (одна на системный code). Ключ включает
// category, чтобы фильтр по категории не перетирал общий кэш.
const EDIT_KEY = [...TEMPLATES_KEY, 'edit'] as const;

export function useTemplatesForEditQuery(category?: TemplateCategory) {
  return useQuery({
    queryKey: [...EDIT_KEY, category ?? 'all'],
    queryFn: () => {
      const q = category ? `?category=${category}` : '';
      return api.get<TemplateForEditList>(`/api/v1/admin/templates${q}`);
    },
    staleTime: 60_000,
  });
}

// M6 — сохранить override текста (PUT). admin-only (server: 403 employee).
// Возвращает обновлённый TemplateForEdit. Инвалидируем родительский префикс
// ['templates'] (а не EDIT_KEY): он покрывает и edit-подключи редактора, и
// базовый ['templates'] справочник, который читает SendMessageDialog. Иначе
// модалка отправки показывала бы старый текст до истечения staleTime.
export function useUpsertTemplateOverrideMutation() {
  const qc = useQueryClient();
  return useMutation({
    mutationKey: ['templates', 'override', 'upsert'],
    mutationFn: ({ code, bodies }: { code: string; bodies: TemplateBodies }) =>
      api.put<TemplateForEdit>(`/api/v1/admin/templates/${code}/override`, { bodies }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: TEMPLATES_KEY });
    },
  });
}

// M6 — сбросить override к системной заготовке (DELETE). admin-only.
export function useDeleteTemplateOverrideMutation() {
  const qc = useQueryClient();
  return useMutation({
    mutationKey: ['templates', 'override', 'delete'],
    mutationFn: (code: string) =>
      api.delete<TemplateForEdit>(`/api/v1/admin/templates/${code}/override`),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: TEMPLATES_KEY });
    },
  });
}
